import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSessionState } from '../hooks/useSessionState';
import {
  Search,
  RotateCcw,
  Eye,
  Loader2,
  RefreshCw,
  ArrowRightCircle,
  Video,
  MapPin,
  MailCheck,
  MailX,
  Clock,
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { MultiSelectCombobox } from '../components/ui/multi-select-combobox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Pagination } from '../components/ui/pagination';
import { SearchableSelect } from '../components/ui/searchable-select';
import { DatePicker } from '../components/ui/date-picker';
import { TimePicker } from '../components/ui/time-picker';
import { useToast } from '../components/ui/toast';
import { getMyTodos, TodoItem } from '../api/todo';
import { assignNextStage, AssignNextStageData } from '../api/stageFlow';
import { getUserList, UserInfo } from '../api/candidate';
import { getCurrentUserInfo } from '../api/user';
import { useUserStore } from '../stores/userStore';

// 可分配的下一环节
const NEXT_STAGES = ['一面', '二面', '三面', '谈薪&背调'] as const;
const INTERVIEW_STAGES = ['一面', '二面', '三面'];

// 线下面试的公司默认地址与交通指引：切到线下时预填，HR 可修改或清空
const DEFAULT_OFFICE_ADDRESS = '成都市武侯区桂溪街道天府大道中段530号东方希望天祥广场B座4003';
const DEFAULT_TRAFFIC_GUIDE = `地铁：1号线、18号线世纪城地铁站B口出来沿着D5商场下穿通道即可到达
公交：附近有天府二街东站、地铁世纪城站B口、天府大道天府二街口站
自驾：请直接导航到东方希望天祥广场大厦B座地下停车场，到停车场坐电梯上1楼，再换乘高层电梯`;

const formatDateTime = (value?: string | null): string => {
  if (!value) return '-';
  return new Date(value).toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

// 等待时长：小于 24 小时按小时显示，超过按天显示并变色提示
const formatWaiting = (hours?: number) => {
  if (hours === undefined || hours === null) return { text: '-', color: 'text-gray-400' };
  if (hours < 24) return { text: `${Math.max(0, Math.round(hours))} 小时`, color: 'text-gray-600' };
  const days = Math.floor(hours / 24);
  return { text: `${days} 天`, color: days >= 2 ? 'text-red-600 font-medium' : 'text-amber-600' };
};

const getResultVariant = (result?: string): 'green' | 'red' | 'amber' | 'gray' => {
  if (result === '通过') return 'green';
  if (result === '不通过' || result === '淘汰') return 'red';
  if (result === '待定') return 'amber';
  return 'gray';
};

// 脱敏展示候选人邮箱
const maskEmail = (email?: string) => {
  if (!email) return '';
  const [name, domain] = email.split('@');
  if (!domain) return email;
  const visible = name.slice(0, Math.min(3, name.length));
  return `${visible}${'*'.repeat(Math.max(1, name.length - visible.length))}@${domain}`;
};

interface AssignForm {
  next_stage: string;
  next_owner_id: string;
  interview_mode: '' | '线上' | '线下';
  date: Date | undefined;
  time: string;
  location: string;
  traffic_guide: string;
  send_email: boolean;
  comments: string;
}

const EMPTY_FORM: AssignForm = {
  next_stage: '',
  next_owner_id: '',
  interview_mode: '',
  date: undefined,
  time: '',
  location: '',
  traffic_guide: '',
  send_email: false,
  comments: '',
};

const StageAssignPage = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { user } = useUserStore();
  const readOnly = user?.role !== 'HR';

  const [loading, setLoading] = useState(false);
  const [todoList, setTodoList] = useState<TodoItem[]>([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [userList, setUserList] = useState<UserInfo[]>([]);
  // 邀约邮件的「联系人」取当前操作人的个人信息，缺失时要提醒，否则候选人收到的邮件里没有联系方式
  const [myContact, setMyContact] = useState<{ phone?: string; email?: string }>({});
  const [keyword, setKeyword] = useSessionState('stageAssignKeyword', '');
  const [selectedStages, setSelectedStages] = useSessionState<string[]>('stageAssignStages', []);
  const [currentPage, setCurrentPage] = useSessionState('stageAssignPage', 1);
  const [pageSize, setPageSize] = useSessionState('stageAssignPageSize', 10);

  // 分配弹窗
  const [dialogOpen, setDialogOpen] = useState(false);
  const [current, setCurrent] = useState<TodoItem | null>(null);
  const [form, setForm] = useState<AssignForm>(EMPTY_FORM);
  const [errors, setErrors] = useState<Partial<Record<keyof AssignForm, string>>>({});
  const [submitting, setSubmitting] = useState(false);

  // 邀约邮件发送失败提示（分配已成功，但邮件没发出去，必须让 HR 手动确认后再关闭）
  const [emailFailure, setEmailFailure] = useState<{
    candidateName: string;
    email: string;
    stage: string;
    reason: string;
  } | null>(null);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getMyTodos({});
      setTodoList(res['待分配'] || []);
      setHasLoaded(true);
    } catch (error) {
      console.error('加载待分配列表失败:', error);
      showToast('加载待分配列表失败', 'error');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadList();
    getUserList()
      .then(setUserList)
      .catch((e) => console.error('加载用户列表失败:', e));
    getCurrentUserInfo()
      .then((me) => setMyContact({ phone: me.phone, email: me.email }))
      .catch((e) => console.error('加载个人信息失败:', e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredAll = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return todoList.filter((item) => {
      if (selectedStages.length && !selectedStages.includes(item.stage || '')) return false;
      if (!kw) return true;
      return (
        (item.candidate_name || '').toLowerCase().includes(kw) ||
        (item.candidate_number || '').toLowerCase().includes(kw) ||
        (item.jd_title || '').toLowerCase().includes(kw) ||
        (item.submitter_name || '').toLowerCase().includes(kw)
      );
    });
  }, [todoList, keyword, selectedStages]);

  useEffect(() => {
    if (!hasLoaded) return;
    const lastPage = Math.max(1, Math.ceil(filteredAll.length / pageSize));
    if (currentPage > lastPage) setCurrentPage(lastPage);
  }, [hasLoaded, filteredAll.length, currentPage, pageSize, setCurrentPage]);

  const pageList = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredAll.slice(start, start + pageSize);
  }, [filteredAll, currentPage, pageSize]);

  const isInterview = INTERVIEW_STAGES.includes(form.next_stage);
  const hasFullSchedule = Boolean(form.interview_mode && form.date && form.time && form.location.trim());
  const canSendEmail = isInterview && hasFullSchedule && Boolean(current?.candidate_email);
  // 邀约邮件里的联系人 = 当前操作人姓名 + 电话 + 邮箱，后两项为空则邮件里不渲染
  const missingContact = [
    !myContact.phone ? '电话' : '',
    !myContact.email ? '邮箱' : '',
  ].filter(Boolean);

  const openDialog = (item: TodoItem) => {
    setCurrent(item);
    setForm(EMPTY_FORM);
    setErrors({});
    setDialogOpen(true);
  };

  // 切换环节：非面试环节清空面试安排
  const handleStageChange = (value: string) => {
    setForm((prev) => ({
      ...prev,
      next_stage: value,
      ...(INTERVIEW_STAGES.includes(value)
        ? {}
        : { interview_mode: '', date: undefined, time: '', location: '', traffic_guide: '', send_email: false }),
    }));
    setErrors((prev) => ({ ...prev, next_stage: undefined }));
  };

  // 切换面试方式：链接与地点共用一列，切换时清空避免残留
  const handleModeChange = (mode: '线上' | '线下') => {
    setForm((prev) => {
      const nextMode = prev.interview_mode === mode ? '' : mode;
      return {
        ...prev,
        interview_mode: nextMode,
        // 线下预填公司地址与交通指引，线上清空（两者共用 interview_location 一列，避免残留）
        location: nextMode === '线下' ? DEFAULT_OFFICE_ADDRESS : '',
        traffic_guide: nextMode === '线下' ? DEFAULT_TRAFFIC_GUIDE : '',
        send_email: false,
      };
    });
  };

  const buildScheduledTime = (): string | null => {
    if (!form.date || !form.time) return null;
    const [h, m] = form.time.split(':');
    const d = new Date(form.date);
    d.setHours(Number(h), Number(m), 0, 0);
    // 后端按本地时间解析，不能带时区后缀
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:00`;
  };

  const handleSubmit = async () => {
    if (!current) return;
    const nextErrors: Partial<Record<keyof AssignForm, string>> = {};
    if (!form.next_stage) nextErrors.next_stage = '请选择下一环节';
    if (!form.next_owner_id) nextErrors.next_owner_id = '请选择下一环节负责人';
    if (isInterview && form.location.trim() && !form.interview_mode) {
      nextErrors.interview_mode = '请先选择面试方式';
    }
    if (isInterview && form.date && !form.time) nextErrors.time = '请选择具体时间';
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    const payload: AssignNextStageData = {
      next_stage: form.next_stage as AssignNextStageData['next_stage'],
      next_owner_id: Number(form.next_owner_id),
      comments: form.comments.trim() || null,
    };
    if (isInterview) {
      payload.interview_mode = form.interview_mode || null;
      payload.scheduled_time = buildScheduledTime();
      payload.interview_location = form.location.trim() || null;
      payload.interview_traffic_guide =
        form.interview_mode === '线下' ? form.traffic_guide.trim() || null : null;
      payload.send_invitation_email = form.send_email && canSendEmail;
    }

    setSubmitting(true);
    try {
      const res = await assignNextStage(current.candidate_id, payload);
      setDialogOpen(false);
      if (payload.send_invitation_email && !res.invitation_sent) {
        // 邮件失败必须让 HR 明确看到：toast 只停留 3 秒，容易一闪而过被当成成功
        setEmailFailure({
          candidateName: current.candidate_name,
          email: current.candidate_email || '',
          stage: form.next_stage,
          reason: res.invitation_error || '未知原因，详见后端日志',
        });
      } else {
        showToast(
          res.invitation_sent
            ? `已分配至${form.next_stage}，面试邀约邮件已发送`
            : `已分配至${form.next_stage}`
        );
      }
      await loadList();
    } catch (error: any) {
      showToast(error?.response?.data?.detail || '分配失败，请稍后重试', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setKeyword('');
    setSelectedStages([]);
    setCurrentPage(1);
  };

  return (
    <div className="flex flex-col h-full gap-4">
      {/* 页面标题 */}
      <div className="flex-shrink-0">
        <h1 className="text-xl font-semibold text-slate-900">待分配</h1>
      </div>

      {/* 筛选卡片 */}
      <Card className="flex-shrink-0">
        <CardContent className="py-3">
          <div className="flex items-center gap-3 flex-wrap">
            {/* 关键词搜索 */}
            <div className="relative w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <Input
                value={keyword}
                onChange={(e) => {
                  setKeyword(e.target.value);
                  setCurrentPage(1);
                }}
                placeholder="搜索姓名、职位"
                className="pl-9 h-8 text-sm text-gray-600"
              />
            </div>

            {/* 已完成环节 */}
            <MultiSelectCombobox
              label="已完成环节"
              options={['简历筛选', '一面', '二面', '三面'].map((s) => ({ label: s, value: s }))}
              value={selectedStages}
              onChange={(val) => {
                setSelectedStages(val);
                setCurrentPage(1);
              }}
              renderItem={(option) => <span>{option.label}</span>}
              renderSelectedItem={(values) => (
                <span className="truncate text-sm">{values.length}个环节</span>
              )}
              placeholder="搜索环节..."
            />

            {/* 重置按钮 */}
            <button
              className="h-8 px-3 text-sm border border-slate-300 text-slate-600 rounded-md hover:bg-slate-50 transition-colors flex items-center gap-1 whitespace-nowrap"
              onClick={handleReset}
            >
              <RotateCcw className="w-3.5 h-3.5" />
              重置
            </button>

            {/* 刷新按钮 */}
            <button
              className="h-8 px-3 text-sm border border-slate-300 text-slate-600 rounded-md hover:bg-slate-50 transition-colors flex items-center gap-1 whitespace-nowrap"
              onClick={loadList}
            >
              <RefreshCw className="w-3.5 h-3.5" />
              刷新
            </button>
          </div>
        </CardContent>
      </Card>

      {/* 统计信息 */}
      <div className="flex items-center justify-end flex-shrink-0">
        <div className="text-sm text-gray-500">
          共 <span className="font-medium text-gray-700">{filteredAll.length}</span> 人待分配
        </div>
      </div>

      {/* 列表 */}
      <Card className="flex-1 flex flex-col min-h-0">
        <CardContent className="pt-4 pb-4 flex-1 flex flex-col min-h-0">
          {loading ? (
            <div className="flex items-center justify-center py-12 flex-1">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : (
            <>
          <div className="relative overflow-x-auto flex-1">
            <Table className="min-w-[1200px]">
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className="w-[100px] min-w-[100px] sticky left-0 bg-gray-50 z-20 whitespace-nowrap">序号</TableHead>
                  <TableHead className="w-[90px] min-w-[90px] sticky left-[100px] bg-gray-50 z-20 whitespace-nowrap border-r">候选人</TableHead>
                  <TableHead className="w-[140px] min-w-[140px] bg-gray-50 whitespace-nowrap">应聘岗位</TableHead>
                  <TableHead className="w-[160px] min-w-[160px] bg-gray-50 whitespace-nowrap">所属部门</TableHead>
                  <TableHead className="w-[110px] min-w-[110px] bg-gray-50 whitespace-nowrap text-center">已完成环节</TableHead>
                  <TableHead className="w-[90px] min-w-[90px] bg-gray-50 whitespace-nowrap text-center">环节结果</TableHead>
                  <TableHead className="w-[100px] min-w-[100px] bg-gray-50 whitespace-nowrap">提交人</TableHead>
                  <TableHead className="w-[150px] min-w-[150px] bg-gray-50 whitespace-nowrap">提交时间</TableHead>
                  <TableHead className="w-[100px] min-w-[100px] bg-gray-50 whitespace-nowrap text-center">已等待</TableHead>
                  <TableHead className="w-[140px] min-w-[140px] sticky right-0 bg-gray-50 z-20 whitespace-nowrap border-l">操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pageList.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="py-12 text-center text-sm text-gray-400">
                      暂无待分配的候选人
                    </TableCell>
                  </TableRow>
                ) : (
                  pageList.map((row) => {
                    const waiting = formatWaiting(row.waiting_hours);
                    return (
                      <TableRow key={row.id}>
                        <TableCell className="py-2 sticky left-0 bg-white z-10 whitespace-nowrap text-gray-500">
                          {row.candidate_number || '-'}
                        </TableCell>
                        <TableCell className="py-2 sticky left-[100px] bg-white z-10 border-r whitespace-nowrap">
                          <button
                            className="text-blue-600 hover:text-blue-700 hover:underline text-left"
                            onClick={() => navigate(`/candidate/detail/${row.candidate_id}`, { state: { from: '/todo/stage-assign' } })}
                          >
                            {row.candidate_name || '-'}
                          </button>
                        </TableCell>
                        <TableCell className="py-2 whitespace-nowrap text-sm text-gray-700">
                          {row.jd_title || '-'}
                        </TableCell>
                        <TableCell className="py-2 whitespace-nowrap text-sm text-gray-500">
                          {row.department || '-'}
                        </TableCell>
                        <TableCell className="py-2 whitespace-nowrap text-center">
                          <Badge variant="gray" size="sm">{row.stage}</Badge>
                        </TableCell>
                        <TableCell className="py-2 whitespace-nowrap text-center">
                          <Badge variant={getResultVariant(row.stage_result)} size="sm">
                            {row.stage_result || '-'}
                          </Badge>
                        </TableCell>
                        <TableCell className="py-2 whitespace-nowrap text-sm text-gray-700">
                          {row.submitter_name || '-'}
                        </TableCell>
                        <TableCell className="py-2 whitespace-nowrap text-sm text-gray-500">
                          {formatDateTime(row.submitted_at || row.created_at)}
                        </TableCell>
                        <TableCell className={`py-2 whitespace-nowrap text-center text-sm ${waiting.color}`}>
                          <span className="inline-flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" />
                            {waiting.text}
                          </span>
                        </TableCell>
                        <TableCell className="py-2 sticky right-0 bg-white z-10 border-l whitespace-nowrap">
                          <div className="flex items-center gap-1 whitespace-nowrap">
                            <button
                              className="px-2 py-1 text-sm text-gray-600 rounded hover:bg-slate-50 transition-colors flex items-center gap-1"
                              onClick={() => navigate(`/candidate/detail/${row.candidate_id}`, { state: { from: '/todo/stage-assign' } })}
                              title="查看候选人详情"
                            >
                              <Eye className="w-3.5 h-3.5" />
                              查看
                            </button>
                            {!readOnly && (
                              <button
                                className="px-2 py-1 text-sm text-blue-600 rounded hover:bg-blue-50 transition-colors flex items-center gap-1"
                                onClick={() => openDialog(row)}
                                title="分配下一环节"
                              >
                                <ArrowRightCircle className="w-3.5 h-3.5" />
                                分配
                              </button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>

              {/* 分页 */}
              {filteredAll.length > 0 && (
                <div className="mt-6">
                  <Pagination
                    currentPage={currentPage}
                    pageSize={pageSize}
                    total={filteredAll.length}
                    onPageChange={setCurrentPage}
                    onPageSizeChange={(size) => {
                      setPageSize(size);
                      setCurrentPage(1);
                    }}
                  />
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* 分配弹窗 */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>分配下一环节</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            {/* 只读回显 */}
            <div className="bg-gray-50 rounded-md p-3.5 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">候选人</span>
                <span className="text-gray-900 font-medium">
                  {current?.candidate_name}
                  {current?.jd_title ? `（${current.jd_title}）` : ''}
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">已完成环节</span>
                <span className="text-gray-900 font-medium">
                  {current?.stage}
                  <Badge variant={getResultVariant(current?.stage_result)} size="sm" className="ml-1.5">
                    {current?.stage_result || '-'}
                  </Badge>
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">提交人</span>
                <span className="text-gray-900 font-medium">{current?.submitter_name || '-'}</span>
              </div>
              {current?.comments && (
                <div className="pt-1 text-xs text-gray-500 leading-relaxed border-t border-gray-200 mt-1">
                  <span className="text-gray-400">评价意见：</span>
                  {current.comments}
                </div>
              )}
            </div>

            {/* 下一环节 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                下一环节 <span className="text-red-500">*</span>
              </label>
              <Select value={form.next_stage} onValueChange={handleStageChange}>
                <SelectTrigger className={errors.next_stage ? 'border-red-500' : ''}>
                  <SelectValue placeholder="请选择下一环节" />
                </SelectTrigger>
                <SelectContent>
                  {NEXT_STAGES.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.next_stage && <p className="mt-1 text-xs text-red-500">{errors.next_stage}</p>}
            </div>

            {/* 负责人 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                下一环节负责人 <span className="text-red-500">*</span>
              </label>
              <SearchableSelect
                options={userList
                  .filter((u) => u.is_active !== false)
                  .map((u) => ({
                    value: String(u.id),
                    label: `${u.id}-${u.real_name || u.username}${u.department ? `（${u.department}）` : ''}`,
                  }))}
                value={form.next_owner_id}
                onValueChange={(value) => {
                  setForm((prev) => ({ ...prev, next_owner_id: value }));
                  setErrors((prev) => ({ ...prev, next_owner_id: undefined }));
                }}
                placeholder="请选择负责人"
                searchPlaceholder="搜索ID、姓名或部门"
                emptyText="没有匹配的负责人"
                className={errors.next_owner_id ? 'border-red-500' : ''}
              />
              {errors.next_owner_id && <p className="mt-1 text-xs text-red-500">{errors.next_owner_id}</p>}
            </div>

            {/* 面试安排（仅面试环节，全部选填） */}
            {isInterview && (
              <div className="rounded-md border border-gray-200 p-3 space-y-3">
                <div className="text-xs font-medium text-gray-500">面试安排（选填）</div>

                <div className="flex gap-2">
                  {(['线上', '线下'] as const).map((mode) => {
                    const active = form.interview_mode === mode;
                    const Icon = mode === '线上' ? Video : MapPin;
                    return (
                      <button
                        key={mode}
                        type="button"
                        onClick={() => handleModeChange(mode)}
                        className={`flex-1 flex items-center justify-center gap-1.5 h-9 rounded-md border text-sm transition-colors ${
                          active
                            ? 'border-blue-500 bg-blue-50 text-blue-600 font-medium'
                            : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        {mode}面试
                      </button>
                    );
                  })}
                </div>
                {errors.interview_mode && (
                  <p className="text-xs text-red-500">{errors.interview_mode}</p>
                )}

                {form.interview_mode && (
                  <>
                    <div>
                      <label className="block text-xs text-gray-500 mb-1.5">面试时间</label>
                      <div className="flex gap-2">
                        <DatePicker
                          date={form.date}
                          onDateChange={(d) => setForm((prev) => ({
                            ...prev,
                            date: d,
                            time: prev.time || '10:00',
                          }))}
                          placeholder="选择日期"
                          className="flex-1"
                        />
                        <TimePicker
                          time={form.time}
                          onTimeChange={(t) => {
                            setForm((prev) => ({ ...prev, time: t }));
                            setErrors((prev) => ({ ...prev, time: undefined }));
                          }}
                          placeholder="选择时间"
                          className="w-32"
                        />
                      </div>
                      {errors.time && <p className="mt-1 text-xs text-red-500">{errors.time}</p>}
                    </div>

                    <div>
                      <label className="block text-xs text-gray-500 mb-1.5">
                        {form.interview_mode === '线上' ? '会议链接' : '面试地点'}
                        {form.interview_mode === '线下' && (
                          <span className="ml-1 text-gray-400">（已预填公司地址，可修改）</span>
                        )}
                      </label>
                      <Input
                        value={form.location}
                        onChange={(e) => setForm((prev) => ({ ...prev, location: e.target.value }))}
                        placeholder={
                          form.interview_mode === '线上'
                            ? '请输入会议链接，如 https://meeting.example.com/xxx'
                            : '请输入面试地点，如 成都市高新区xx大厦12楼前台'
                        }
                      />
                    </div>

                    {form.interview_mode === '线下' && (
                      <div>
                        <label className="block text-xs text-gray-500 mb-1.5">
                          交通指引
                          <span className="ml-1 text-gray-400">（已预填公司默认指引，可修改）</span>
                        </label>
                        <Textarea
                          value={form.traffic_guide}
                          onChange={(e) => setForm((prev) => ({ ...prev, traffic_guide: e.target.value }))}
                          placeholder="地铁 / 公交 / 自驾路线，会原样写进候选人邀约邮件"
                          rows={4}
                          className="text-sm"
                        />
                      </div>
                    )}

                    <label
                      className={`flex items-start gap-2 rounded-md px-2.5 py-2 text-sm ${
                        canSendEmail ? 'bg-blue-50 cursor-pointer' : 'bg-gray-50 cursor-not-allowed'
                      }`}
                    >
                      <input
                        type="checkbox"
                        className="mt-0.5"
                        disabled={!canSendEmail}
                        checked={form.send_email && canSendEmail}
                        onChange={(e) => setForm((prev) => ({ ...prev, send_email: e.target.checked }))}
                      />
                      <span className={canSendEmail ? 'text-blue-700' : 'text-gray-400'}>
                        <span className="inline-flex items-center gap-1">
                          <MailCheck className="w-3.5 h-3.5" />
                          发送面试邀约邮件给候选人
                        </span>
                        <span className="block mt-0.5 text-xs">
                          {!current?.candidate_email
                            ? '该候选人未解析到邮箱，无法发送'
                            : !hasFullSchedule
                            ? '填写面试方式、时间和链接/地点后可发送'
                            : `将发送至 ${maskEmail(current?.candidate_email)}`}
                        </span>
                      </span>
                    </label>

                    {canSendEmail && missingContact.length > 0 && (
                      <p className="text-xs text-amber-600 leading-relaxed">
                        提醒：你的个人信息未填写{missingContact.join('和')}，
                        邮件里的「联系人」只会显示姓名，候选人无法与你取得联系
                        （邮件页脚已声明请勿回复本邮件）。建议先到「个人信息」补全。
                      </p>
                    )}
                  </>
                )}
              </div>
            )}

            {/* 备注 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">备注</label>
              <Textarea
                value={form.comments}
                onChange={(e) => setForm((prev) => ({ ...prev, comments: e.target.value }))}
                placeholder="选填，会记录到招聘日志"
                rows={2}
              />
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={submitting}>
                取消
              </Button>
              <Button
                className="bg-blue-600 hover:bg-blue-700"
                onClick={handleSubmit}
                disabled={submitting || !form.next_stage || !form.next_owner_id}
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                    {form.send_email && canSendEmail ? '提交并发送邮件...' : '提交中...'}
                  </>
                ) : (
                  '确认分配'
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* 邀约邮件发送失败：分配已生效，但候选人没收到邮件，必须让 HR 手动确认后再关闭 */}
      <Dialog open={Boolean(emailFailure)} onOpenChange={(open) => !open && setEmailFailure(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <MailX className="w-5 h-5" />
              面试邀约邮件发送失败
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-gray-700 leading-relaxed">
              <span className="font-medium">{emailFailure?.candidateName}</span> 已成功分配至
              <span className="font-medium">{emailFailure?.stage}</span>，
              但邀约邮件<span className="font-medium text-red-600">没有发出去</span>，
              候选人并不知道面试安排，请电话或其他方式手动通知。
            </p>
            <div className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-700 leading-relaxed break-all">
              <div className="font-medium mb-1">失败原因</div>
              {emailFailure?.reason}
            </div>
            {emailFailure?.email && (
              <p className="text-xs text-gray-500">收件地址：{maskEmail(emailFailure.email)}</p>
            )}
            <p className="text-xs text-gray-500 leading-relaxed">
              该状态已记录，「待面试」列表的面试安排列会用红色邮件图标标出，方便事后核对补发。
            </p>
          </div>
          <div className="flex justify-end pt-1">
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => setEmailFailure(null)}>
              知道了
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StageAssignPage;
